import React, { useState, useEffect } from 'react';
import { Star, MessageCircle, Calendar, User } from 'lucide-react';

const ReviewList = ({ 
  providerId, 
  serviceId, 
  productId, 
  showProviderResponse = true,
  maxReviews = null 
}) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchReviews();
    if (providerId) {
      fetchReviewSummary();
    }
  }, [providerId, serviceId, productId]);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (providerId) {
        endpoint = `/api/reviews/provider/${providerId}`;
      } else if (serviceId) {
        endpoint = `/api/reviews/service/${serviceId}`;
      } else if (productId) {
        endpoint = `/api/reviews/product/${productId}`;
      } else {
        setError('No ID provided for fetching reviews');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReviews(maxReviews ? data.slice(0, maxReviews) : data);
      } else {
        setError('Failed to load reviews');
      }
    } catch (err) {
      setError('Network error loading reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/summary/${providerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching review summary:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const renderRatingDistribution = () => {
    if (!summary || summary.totalReviews === 0) return null;

    return (
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Customer Reviews</h3>
          <div className="flex items-center space-x-2">
            {renderStars(Math.round(summary.averageRating))}
            <span className="text-lg font-semibold text-gray-900">{summary.averageRating}</span>
            <span className="text-sm text-gray-600">({summary.totalReviews} reviews)</span>
          </div>
        </div>
        
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = summary.ratingDistribution[rating] || 0;
            const percentage = summary.totalReviews > 0 ? (count / summary.totalReviews) * 100 : 0;
            
            return (
              <div key={rating} className="flex items-center space-x-2 text-sm">
                <span className="w-8 text-gray-600">{rating} ★</span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="w-8 text-gray-600 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Review Summary */}
      {providerId && renderRatingDistribution()}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No reviews yet. Be the first to leave a review!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{review.userName}</h4>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(review.createdAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {renderStars(review.rating)}
                  <p className="text-xs text-gray-500 mt-1">
                    {review.isVerifiedPurchase && '✓ Verified Purchase'}
                  </p>
                </div>
              </div>

              {/* Service/Product Info */}
              {(review.serviceTitle || review.productName) && (
                <div className="mb-3 text-sm text-gray-600">
                  <span className="font-medium">
                    {review.serviceTitle ? `Service: ${review.serviceTitle}` : `Product: ${review.productName}`}
                  </span>
                </div>
              )}

              {/* Review Comment */}
              <div className="mb-3">
                <p className="text-gray-700 leading-relaxed">{review.comment}</p>
              </div>

              {/* Provider Response */}
              {showProviderResponse && review.providerResponse && (
                <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-200 rounded">
                  <div className="flex items-center mb-2">
                    <div className="flex items-center justify-center w-6 h-6 bg-blue-200 rounded-full mr-2">
                      <span className="text-xs font-semibold text-blue-800">P</span>
                    </div>
                    <span className="text-sm font-medium text-blue-800">
                      Response from {review.providerName || 'Provider'}
                    </span>
                    <span className="text-xs text-blue-600 ml-2">
                      {formatDate(review.providerResponseAt)}
                    </span>
                  </div>
                  <p className="text-sm text-blue-700 pl-8">{review.providerResponse}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Show More Link */}
      {maxReviews && reviews.length >= maxReviews && (
        <div className="text-center pt-4 border-t border-gray-200">
          <button 
            onClick={() => window.location.href = `/reviews?${providerId ? `provider=${providerId}` : serviceId ? `service=${serviceId}` : `product=${productId}`}`}
            className="text-blue-600  font-medium"
          >
            View All Reviews ({summary?.totalReviews || reviews.length}+)
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewList;